import React, { useState, useRef, useEffect, useMemo } from "react";

import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@material-tailwind/react";

import useEvents from "@datahooks/events/useEvents";
import useEventLinkManager from "@datahooks/events/useEventLinkManager";

import { useProjectContext } from "@datahooks/ProjectContext";
import { useReleaseContext } from "@datahooks/ReleaseContext";

import { TestableButton, TestableText, TestableChip } from "@components/general/TestableComponents";
import { severityColours, severityRank } from "./events";
import RichSelectableList from "../../general/RichSelectableList";
import LoadingIcon from "../../navigation/LoadingIcon";
import AlertManager from "../../general/AlertManager";
import FilterBar, { getOptions, getPriorityOptions } from "@components/cards/shared/FilterBar.jsx";

import PropTypes from "prop-types";

export default function AttachTestEvent({ isOpen, onClose, onAttached, testTypeId, excludeEventIds, testName, additionalId }) {
  const dialogRef = useRef(null);

  const eventUpdated = (result) => {
    //Close the dialog if it is still open
    if (onClose && typeof onClose === 'function') {
      onClose();
    }

    //Handle attachment event in parent
    if (onAttached && typeof onAttached === 'function') {
      onAttached(result);
    }
  }

  //After a failed save
  const eventSaveFailed = (error) => {
    setErrorMessage(`Failed to save event: ${error?.errorResponse?.message || 'Unknown error'}`);
  }

  const { projectId } = useProjectContext();
  const { release } = useReleaseContext();

  const [ selectedEvent, setSelectedEvent ] = useState(null);

  const { events, isLoading, error, refetch } = useEvents('Test', projectId, true, release?.release_id, testTypeId);

  const [filters, setFilters] = useState({severity: '', eventType: '' });

  const eventTypeOptions = useMemo(() => getOptions(events, 'title'), [events]);
  const priorityOptions = useMemo(() => getPriorityOptions(events, 'severity'), [events]);

  useEffect(() => {
    if (isOpen && refetch) {
      refetch();
    }
  }, [isOpen, refetch]);
  
  const [errorMessage, setErrorMessage] = useState(null);

  //Hook for performing the save action
  const { performAction } = useEventLinkManager(eventUpdated, eventSaveFailed);

  const uniqueEvents = useMemo(() => {
    const seen = new Set();
    return (events || []).filter(event => {
      if (seen.has(event.id)) return false;
      seen.add(event.id);
      return true;
    });
  }, [events]);  

  const excludedEventIds = useMemo(() => new Set(excludeEventIds ?? []), [excludeEventIds]);

  const filteredEvents = uniqueEvents?.filter((event) => !excludedEventIds.has(event.id));

  const filteredItems = filteredEvents?.filter(item => {
      const matchesPriority = !filters.severity || item.severity === filters.severity;
      // Get events for the selected filter
      const matchesEventType = !filters.eventType || item.title === filters.eventType;
  
      return matchesPriority && matchesEventType;
    }) || [];

  //Sort events so events without an end date are top, then sort by severity, then start time if necessary
  filteredItems?.sort((a, b) => {
    const aEndTime = a.end_time ? new Date(a.end_time) : null;
    const bEndTime = b.end_time ? new Date(b.end_time) : null;

    // Sort by end time (null first)
    if (!aEndTime && bEndTime) return -1;
    if (aEndTime && !bEndTime) return 1;
    if (!aEndTime && !bEndTime) return 0;

    // Sort by severity
    const aSeverity = severityRank[a.severity] || 0;
    const bSeverity = severityRank[b.severity] || 0;
    if (aSeverity !== bSeverity) return bSeverity - aSeverity;

    // Sort by start time
    return new Date(b.start_time) - new Date(a.start_time);
  });

  //Set AlertManager if a data loading error occurs
  useEffect(() => {
    if (error) {
      setErrorMessage(`Error loading events: ${error.message || "Unknown error"}`);
    }
  }, [error]);

  useEffect(() => {
    if (dialogRef.current && errorMessage) {
      dialogRef.current.scrollTo({
        top: 0,
        behavior: 'auto'
      });
    }
  }, [errorMessage]);

  const doClose = () => {
    setSelectedEvent(null);
    ((onClose && typeof onClose === 'function') && onClose());
  }

  const doEventAttach = () => {

    let eventObj = {
      event_id: selectedEvent.id,
      test_name: testName,
      additional_id: additionalId,
    };

    //Perform the save action
    performAction(eventObj,'POST');
  }

  return (
    <Dialog data-testid="dialog-attach-test-event" open={isOpen} size="md" handler={doClose}>
      <DialogHeader>
        <TestableText dataTestId="date-header-text" variant="h5" className="text-blue-500">Attach Existing Event</TestableText>
      </DialogHeader>
      <DialogBody ref={dialogRef} className="max-h-[60vh] overflow-y-auto pr-1">
        {(isLoading) && (
          <div className="flex items-center justify-center h-32">
            <LoadingIcon showText />
          </div>
        )}

        {errorMessage && (
          <AlertManager errorMessage={errorMessage} setErrorMessage={setErrorMessage} successMessage="" setSuccessMessage={() => {}} />
        )}

        {!isLoading && filteredEvents?.length === 0 && (
          <TestableText dataTestId="date-no-events" variant="h6" className="p-4 text-center text-gray-500">
            No events available to attach.
          </TestableText>
        )}

        {!isLoading && filteredEvents?.length > 0 && (
          <>
            <FilterBar
              filters={filters}
              onChange={setFilters}
              fields={[
                { key: 'eventType', label: 'Event Type', type: 'dropdown', options: eventTypeOptions },
                { key: 'severity', label: 'Severity', type: 'dropdown', options: priorityOptions, transform: (value) => value.toUpperCase(), }, //Uppercase the value
              ]}
            />

            <RichSelectableList
              items={filteredItems}
              selectedItem={selectedEvent}
              onSelect={setSelectedEvent}
              renderSummary={(item) => (
                <div className="flex items-center justify-between w-full break-words min-w-0">
                  <TestableText dataTestId="date-event-header-title" variant="h6" className="min-w-0 break-words">{`${item.title}`}</TestableText>
                  <TestableChip dataTestId="date-event-severity-chip" color={severityColours[item.severity]} value={item.severity} />
                </div>
              )}

              renderDetails={(item) => (
                <>
                  <div className="flex items-center justify-between gap-2 w-full min-w-0">
                    <TestableText dataTestId="date-event-detail-testname" variant="h6" className="min-w-0 break-words">{item.testName}</TestableText>
                    {item.area && (
                      <span data-testid="date-event-detail-area" className="px-2 py-0.5 rounded border border-gray-400 whitespace-nowrap flex-shrink-0">
                        {item.area}
                      </span>
                    )}
                  </div>

                  {item.event_description && 
                    <div 
                      data-testid="date-event-detail-description" 
                      className="min-w-0 whitespace-pre-wrap break-words [overflow-wrap:anywhere] max-h-40 overflow-auto font-mono text-sm">
                        {item.event_description}
                      </div>}

                  <div className="flex flex-row gap-2 w-full mt-2">
                    <TestableText dataTestId="date-event-detail-starting-release">{`Start: ${item.formatted_start_time} (${item.starting_release})`}</TestableText>
                    {item.end_release && ( 
                      <TestableText dataTestId="date-event-details-end-release" className="ml-auto">{`Resolved: ${item.formatted_end_time} (${item.end_release})`}</TestableText>
                    )}
                  </div>
                </>
              )}

            />
          </>
        )}

      </DialogBody>
      <DialogFooter className="gap-x-2">
        <TestableButton dataTestId="date-footer-cancel-button" variant="outlined" className="text-blue-500" onClick={doClose}>
          Cancel
        </TestableButton>
        <TestableButton dataTestId="date-footer-attach-button" color="blue" onClick={doEventAttach} disabled={!selectedEvent}>
          Attach
        </TestableButton>
      </DialogFooter>
    </Dialog>
  );
}

AttachTestEvent.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAttached: PropTypes.func.isRequired,
  testTypeId: PropTypes.number.isRequired,
  excludeEventIds: PropTypes.arrayOf(PropTypes.number),
  testName: PropTypes.string.isRequired,
  additionalId: PropTypes.string
};